import { useEffect, useId, useMemo, useRef, useState } from "react";

export type FifaThemeDropdownOption = {
  value: string;
  label: string;
  shortLabel?: string;
};

type FifaThemeDropdownProps = {
  value: string;
  onChange: (value: string) => void;
  options: FifaThemeDropdownOption[];
  placeholder?: string;
  disabled?: boolean;
  searchable?: boolean;
  className?: string;
  ariaLabel?: string;
};

const FifaThemeDropdown = ({
  value,
  onChange,
  options,
  placeholder = "SELECT",
  disabled = false,
  searchable = true,
  className = "",
  ariaLabel,
}: FifaThemeDropdownProps) => {
  const listboxId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value]
  );

  const displayText = selectedOption
    ? selectedOption.shortLabel || selectedOption.label
    : placeholder;

  const filteredOptions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return options;
    return options.filter((option) => option.label.toLowerCase().includes(query));
  }, [options, searchQuery]);

  const openDropdown = () => {
    if (disabled) return;
    setIsOpen(true);
    setSearchQuery("");
  };

  const closeDropdown = () => {
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleSelect = (nextValue: string) => {
    onChange(nextValue);
    closeDropdown();
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange("");
    closeDropdown();
  };

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        closeDropdown();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && searchable) {
      searchRef.current?.focus();
    }
  }, [isOpen, searchable]);

  return (
    <div
      ref={containerRef}
      className={`fifaThemeDropdown ${isOpen ? "fifaThemeDropdown--open" : ""} ${disabled ? "fifaThemeDropdown--disabled" : ""} ${className}`.trim()}
    >
      <div className="fifaThemeDropdown__control">
        {isOpen && searchable ? (
          <input
            ref={searchRef}
            type="text"
            className="fifaThemeDropdown__search"
            value={searchQuery}
            placeholder={selectedOption?.label || placeholder}
            aria-label={ariaLabel || placeholder}
            onChange={(event) => setSearchQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                closeDropdown();
              }
            }}
          />
        ) : (
          <button
            type="button"
            className="fifaThemeDropdown__display"
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-controls={listboxId}
            onClick={openDropdown}
            title={selectedOption?.label || placeholder}
          >
            <span className="fifaThemeDropdown__displayText">{displayText}</span>
          </button>
        )}

        <div className="fifaThemeDropdown__actions">
          {value && !disabled && (
            <button
              type="button"
              className="fifaThemeDropdown__clearBtn"
              aria-label="Clear selection"
              onClick={handleClear}
            >
              ×
            </button>
          )}
          <button
            type="button"
            className="fifaThemeDropdown__toggleBtn"
            disabled={disabled}
            aria-label={isOpen ? "Close options" : "Open options"}
            onClick={() => (isOpen ? closeDropdown() : openDropdown())}
          >
            <svg
              width="16"
              height="10"
              viewBox="0 0 16 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M1.5 1.5L8 8L14.5 1.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      {isOpen && (
        <ul
          id={listboxId}
          className="fifaThemeDropdown__panel"
          role="listbox"
          aria-label={ariaLabel || placeholder}
        >
          {filteredOptions.length === 0 ? (
            <li className="fifaThemeDropdown__empty">No matches found</li>
          ) : (
            filteredOptions.map((option) => {
              const isSelected = option.value === value;
              return (
                <li key={option.value} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={`fifaThemeDropdown__option ${isSelected ? "fifaThemeDropdown__option--selected" : ""}`}
                    onClick={() => handleSelect(option.value)}
                  >
                    {option.label}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
};

export default FifaThemeDropdown;
